import { fetchWithClerkJwt } from "./fetch-with-clerk";

const ME_SAVED_ADDRESSES = "/api/v1/me/saved-addresses";

export type CustomerSavedAddress = {
  id: string;
  label: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CustomerSavedAddressesResponse = {
  addresses: CustomerSavedAddress[];
};

export type CreateCustomerSavedAddressBody = {
  label?: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  zip: string;
  isDefault?: boolean;
};

export type PatchCustomerSavedAddressBody = Partial<CreateCustomerSavedAddressBody> & {
  isDefault?: boolean;
};

export async function fetchCustomerSavedAddresses(
  getToken: () => Promise<string | null>,
): Promise<CustomerSavedAddressesResponse> {
  const res = await fetchWithClerkJwt(getToken, ME_SAVED_ADDRESSES);
  if (!res.ok) {
    let body: { error?: string; message?: string } | undefined;
    try {
      body = (await res.json()) as { error?: string; message?: string };
    } catch {
      /* ignore */
    }
    if (body?.error === "saved_addresses_schema_missing" && typeof body.message === "string") {
      throw new Error(body.message);
    }
    throw new Error(`saved_addresses_http_${res.status}`);
  }
  return (await res.json()) as CustomerSavedAddressesResponse;
}

export async function createCustomerSavedAddress(
  getToken: () => Promise<string | null>,
  body: CreateCustomerSavedAddressBody,
): Promise<CustomerSavedAddress> {
  const res = await fetchWithClerkJwt(getToken, ME_SAVED_ADDRESSES, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ...body, state: "CO" as const }),
  });
  if (!res.ok) {
    throw new Error(`saved_address_create_http_${res.status}`);
  }
  return (await res.json()) as CustomerSavedAddress;
}

export async function patchCustomerSavedAddress(
  getToken: () => Promise<string | null>,
  addressId: string,
  body: PatchCustomerSavedAddressBody,
): Promise<CustomerSavedAddress> {
  const path = `${ME_SAVED_ADDRESSES}/${encodeURIComponent(addressId)}`;
  const res = await fetchWithClerkJwt(getToken, path, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`saved_address_patch_http_${res.status}`);
  }
  return (await res.json()) as CustomerSavedAddress;
}

export async function deleteCustomerSavedAddress(
  getToken: () => Promise<string | null>,
  addressId: string,
): Promise<void> {
  const path = `${ME_SAVED_ADDRESSES}/${encodeURIComponent(addressId)}`;
  const res = await fetchWithClerkJwt(getToken, path, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(`saved_address_delete_http_${res.status}`);
  }
}
